import {forwardRef} from 'react';
import type {ComponentType, HTMLAttributes, ReactNode} from 'react';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '../lib/utils';

const dialogHeroIconVariants = cva(
  'size-12 rounded-full flex items-center justify-center border',
  {
    variants: {
      iconVariant: {
        success:
          'bg-success-muted border-success-border text-success-foreground',
        destructive: 'bg-destructive/10 border-destructive/20 text-destructive',
        accent: 'bg-accent border-border text-accent-foreground',
        card: 'bg-card border-border text-muted-foreground',
      },
    },
    defaultVariants: {
      iconVariant: 'success',
    },
  },
);

type IconComponent = ComponentType<{className?: string}>;

interface DialogHeroProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof dialogHeroIconVariants> {
  Icon: IconComponent;
  title: ReactNode;
  subtitle?: ReactNode;
}

const DialogHero = forwardRef<HTMLDivElement, DialogHeroProps>(
  (
    {className, iconVariant, Icon, title, subtitle, children, ...props},
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col items-center gap-6', className)}
        {...props}
      >
        <div className={dialogHeroIconVariants({iconVariant})}>
          <Icon className="size-5" />
        </div>

        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-center">{title}</h2>
          {subtitle && (
            <p className="text-sm text-foreground text-center">{subtitle}</p>
          )}
        </div>

        {children}
      </div>
    );
  },
);
DialogHero.displayName = 'DialogHero';

export {DialogHero, dialogHeroIconVariants};
export type {DialogHeroProps};
